import { ApolloError, ApolloQueryResult, useQuery } from '@apollo/client';
import { useMemo } from 'react';

import { isDemoEnvironment } from '../../../../constants/global';
import { handleGql403 } from '../../../../utils';
import { AdAccountsByUserQuery, AdAccountsByUserQueryVariables } from '../../types';
import { GET_AD_ACCOUNTS_BY_USER } from '../getAdAccountsByUser';
import { MockedAdAccountsByUser, mockForDemoAdAccountsList } from './mock';
import { getBrandItems } from './utils';

export type AdAccountItem = {
  id: string;
  label: string;
  isBaymaxEnabled: boolean;
};

export type BrandItem = {
  name: string;
  id: string;
  accountList: AdAccountItem[];
};

export type AdAccountByUserResponse = {
  adAccountsList: BrandItem[];
  loading: boolean;
  error?: ApolloError;
  brandsData?: AdAccountsByUserQuery;
  refetch(): Promise<ApolloQueryResult<AdAccountsByUserQuery>>;
};

const useFetchAdAccountByUser = (
  id?: string,
  orgId?: string,
  isGhostMode: boolean = false
): AdAccountByUserResponse => {
  const { data, loading, error, refetch } = useQuery<AdAccountsByUserQuery, AdAccountsByUserQueryVariables>(
    GET_AD_ACCOUNTS_BY_USER,
    {
      variables: {
        userId: String(id),
      },
      skip: !id || !orgId || isGhostMode || isDemoEnvironment,
      onError: handleGql403,
    }
  );

  const adAccountsList: BrandItem[] = useMemo((): BrandItem[] => {
    if (!data?.adAccountsByUser?.edges || !orgId) return [];
    return getBrandItems(orgId, data.adAccountsByUser.edges);
  }, [data, orgId]);

  return {
    adAccountsList: isDemoEnvironment ? mockForDemoAdAccountsList : adAccountsList,
    brandsData: isDemoEnvironment ? MockedAdAccountsByUser : data,
    error,
    loading: loading,
    refetch,
  };
};

export default useFetchAdAccountByUser;
